import { computed, ref } from 'vue';
import { useUrlsStore } from '@/store/urlsStore';
import type IUrl from '@/interfaces/IUrl';
import UrlFetchStatus from '@/enums/UrlFetchStatus';

export function useUrlPanel(filterFn: (url: IUrl) => boolean) {
    const urlsStore = useUrlsStore();

    // Filter all URLs with the provided filtering function.
    const allUrls = computed(() =>
        (urlsStore.urls || []).filter(filterFn)
    );

    // Active fetch status filters.
    const activeFetchStatusFilters = ref<UrlFetchStatus[]>([]);

    const toggleFetchStatusFilter = (status: UrlFetchStatus) => {
        const index = activeFetchStatusFilters.value.indexOf(status);
        if (index > -1) {
            activeFetchStatusFilters.value.splice(index, 1);
        } else {
            activeFetchStatusFilters.value.push(status);
        }
    };

    // Filter URLs based on the active fetch status filters.
    const filteredUrls = computed(() => {
        if (activeFetchStatusFilters.value.length === 0) {
            return allUrls.value;
        }
        return allUrls.value.filter(url => {
            // Treat missing fetchStatus as NOT_FETCHED.
            const status = url.fetchStatus || UrlFetchStatus.TO_FETCH;
            return activeFetchStatusFilters.value.includes(status);
        });
    });

    // Compute counts for each fetch status.
    const fetchStatusCounts = computed(() => {
        const counts: Record<UrlFetchStatus, number> = {
            [UrlFetchStatus.NOT_FETCHED]: 0,
            [UrlFetchStatus.TO_FETCH]: 0,
            [UrlFetchStatus.FETCHED]: 0,
            [UrlFetchStatus.IN_PROGRESS]: 0,
            [UrlFetchStatus.FAILED]: 0,
        };
        allUrls.value.forEach(url => {
            const status = url.fetchStatus || UrlFetchStatus.TO_FETCH;
            counts[status] = (counts[status] || 0) + 1;
        });
        return counts;
    });

    return {
        allUrls,
        activeFetchStatusFilters,
        toggleFetchStatusFilter,
        filteredUrls,
        fetchStatusCounts,
    };
}
